import React, { useState } from 'react';
import {
  IAuditoriaServicosTerceirosPfResultado,
  IItemServicosTerceirosPf,
} from '../interfaces/IAuditoriaServicosTerceirosPf';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaServicosTerceirosPfResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    case 'PENDENTE':
      return 'bi-question-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-207: rótulo humano por item — evita que o usuário confunda "ALERTA" (vocabulário de
// severidade padrão do dashboard) com uma confirmação textual, e deixa explícito que PENDENTE é
// indício, não confirmação, de Pessoa Física/RPA.
const getRotuloClassificacao = (status: string) => {
  switch (status) {
    case 'ALERTA':
      return 'PF/RPA Confirmado';
    case 'PENDENTE':
      return 'Indício — Pendente de Validação';
    default:
      return status;
  }
};

// COR-208: ordem de exibição dos grupos de categoria — a conta principal primeiro, depois as
// categorias específicas de maior volume esperado, "Outros" sempre por último.
const ORDEM_CATEGORIAS = [
  'Serviços Prestados por Terceiros',
  'Tecnologia / TI',
  'Internet / Hospedagem',
  'Alimentação',
  'Despesas Bancárias',
  'Outros Potenciais Indícios',
];

const AuditoriaServicosTerceirosPfDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const lancamentos = resultado.lancamentosServicosTerceirosPf || [];

  // COR-208: agrupa por categoria/natureza contábil — a regra foi ampliada (COR-147) para várias
  // naturezas de despesa além de "Serviços Prestados por Terceiros"; agrupar deixa isso visível em
  // vez de apresentar tudo como se fosse uma única conta referencial.
  const grupos = ORDEM_CATEGORIAS
    .map((categoria) => ({
      categoria,
      itens: lancamentos.filter((item) => (item.categoria || 'Outros Potenciais Indícios') === categoria),
    }))
    .filter((g) => g.itens.length > 0);

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Serviços de Terceiros (Pessoa Física) — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Risco Tributário (Dashboard)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          <div className='auditoria-kpi-help'>Soma de INSS Patronal + INSS Autônomo + IRRF de todos os pagamentos identificados.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / Confirmados</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>
          </div>
          <div className='auditoria-kpi-help'>Pagamentos com evidência objetiva de pessoa física/RPA sem CPF no histórico, em conta de serviços a terceiros.</div>
        </div>

        <div className={`auditoria-kpi ${resultado.totalPendentes > 0 ? 'auditoria-kpi-alerta' : ''}`}>
          <div className='auditoria-kpi-label'>Indício — Pendentes de Classificação</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-pendente'>{resultado.totalPendentes}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Nome no histórico não indica claramente Pessoa Jurídica, mas também não há evidência objetiva de Pessoa
            Física/RPA — requer validação manual do cadastro do participante, sem risco calculado automaticamente.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Descartados (PJ / Fornecedor Conhecido)</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-secundario'>{resultado.totalDescartados}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Lançamentos avaliados e descartados por evidência de Pessoa Jurídica, CPF já citado no histórico, ou
            fornecedor de software/plataforma/tecnologia conhecido — não representam risco.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base contábil, fiscal e orientação de justificativa (capa da conciliação).</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div className='mb-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
          <div className='mb-2'><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div><strong>Orientação de Preenchimento da Justificativa:</strong> {resultado.orientacaoJustificativa}</div>
        </div>
      )}

      <div className='auditoria-card'>
        {/* COR-207/COR-208: título anterior ("Pagamentos a Pessoa Física Identificados... Conta
            Referencial 3.01.01.01.01.04.03") afirmava confirmação e limitava a análise a uma única
            conta referencial, mesmo a regra (desde a COR-147) analisando várias naturezas de
            despesa (Tecnologia/TI, Internet/Hospedagem, Alimentação, Despesas Bancárias etc.) em
            busca de indício de pagamento a PF/RPA. Título e descrição agora deixam isso explícito. */}
        <h3>Pagamentos a Pessoa Física/RPA — Confirmados e Pendentes de Classificação</h3>
        <p className='text-muted'>
          Esta auditoria monitora <strong>múltiplas categorias de conta</strong> com potencial indício de pagamento a
          pessoa física/RPA — não apenas a conta principal de Serviços Prestados por Terceiros (ver categorias
          abaixo). <strong>PF/RPA Confirmado</strong>: evidência objetiva no histórico (termos "RPA", "autônomo" ou
          "pessoa física") — risco tributário calculado, exige validação da emissão do RPA e do recolhimento de
          INSS (Empresa/Autônomo), IRRF e ISS. <strong>Indício — Pendente de Validação</strong>: nome no histórico não
          indica claramente Pessoa Jurídica, mas sem evidência objetiva de PF/RPA — requer validação manual do
          cadastro do participante antes de qualquer cálculo de risco.
        </p>

        {lancamentos.length === 0 && (
          <div className='text-muted mt-4'>Nenhum pagamento a pessoa física sem CPF no histórico identificado em conta de serviços a terceiros no período.</div>
        )}

        {grupos.map((grupo) => (
          <div key={grupo.categoria} className='mt-4'>
            <h4 style={{ fontSize: 15 }}>
              {grupo.categoria} <span className='auditoria-contador auditoria-contador-secundario'>{grupo.itens.length}</span>
            </h4>
            <div className='table-responsive'>
              <table className='auditoria-table'>
                <thead>
                  <tr>
                    <th>Conta</th>
                    <th>Data</th>
                    <th>Histórico</th>
                    <th className='text-end'>Valor Bruto</th>
                    <th className='text-end'>Risco Tributário</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {grupo.itens.map((item: IItemServicosTerceirosPf) => {
                    const chave = `stpf-${item.empresaId}-${item.contaContabil}-${item.dataLancamento}-${item.historico}`;
                    const expandido = itemExpandido === chave;

                    return (
                      <React.Fragment key={chave}>
                        <tr className={`auditoria-row-tributo status-${item.status}`} onClick={() => alternarExpandir(chave)}>
                          <td>
                            <strong>{item.descricaoConta || item.contaContabil}</strong>
                            <div className='text-muted' style={{ fontSize: 11 }}>
                              {item.contaContabil} ({item.contaReferencial})
                              {!item.referencialConfiavel && (
                                <i
                                  className='bi bi-exclamation-triangle-fill ms-1 text-warning'
                                  title='Referencial da ECD pode não corresponder a esta conta — ver motivo de inclusão no detalhe da linha.'
                                />
                              )}
                            </div>
                          </td>
                          <td>{formatDate(item.dataLancamento)}</td>
                          <td style={{ maxWidth: 260 }}>{item.historico}</td>
                          <td className='text-end'>{formatCurrency(item.valor)}</td>
                          <td className='text-end'>{formatCurrency(item.riscoTributarioTotal)}</td>
                          <td>
                            <span className={`auditoria-status status-${item.status}`}>{getRotuloClassificacao(item.status)}</span>
                            <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                          </td>
                        </tr>

                        {expandido && (
                          <tr>
                            <td colSpan={6} style={{ background: '#fffbeb' }}>
                              <div className='py-2 px-3'>
                                <div className={`auditoria-alert status-${item.status} mb-2`}>
                                  <i className={`bi ${getIconeStatus(item.status)} auditoria-alert-icon`} />
                                  <div>{item.mensagemObrigatoria}</div>
                                </div>

                                <div className='table-responsive mb-2'>
                                  <table className='auditoria-table'>
                                    <thead>
                                      <tr>
                                        <th>INSS Patronal (20%)</th>
                                        <th>INSS Autônomo (11%)</th>
                                        <th>IRRF</th>
                                        <th>ISS</th>
                                        <th>Risco Tributário Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>{formatCurrency(item.inssPatronal)}</td>
                                        <td>{formatCurrency(item.inssAutonomo)}</td>
                                        <td>{formatCurrency(item.irrf)}</td>
                                        <td>{formatCurrency(item.iss)}</td>
                                        <td><strong>{formatCurrency(item.riscoTributarioTotal)}</strong></td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>

                                <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                                {/* COR-208: transparência de por que a conta e o lançamento entraram na regra —
                                    referencial contábil / nome da conta / termo genérico de despesa (conta), e
                                    termo de risco identificado (lançamento). */}
                                <div className='mt-2'><strong>Motivo de inclusão da conta:</strong> {item.motivoInclusaoConta}</div>
                                <div><strong>Termo de risco identificado no lançamento:</strong> {item.termoRiscoIdentificado}</div>
                                <div className='mt-2'><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                                <div className='mt-2'><strong>Orientação de Preenchimento da Justificativa:</strong> {resultado.orientacaoJustificativa}</div>
                                <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AuditoriaServicosTerceirosPfDetalhe;
